import { FC, useMemo } from 'react'
import styles from 'components/features/creditCard/creditCardPage/RecentTransactions/RecentTransactions.styl'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { ReactNode } from 'react-redux'
import { TransactionNarrowGroup } from 'components/features/pfm/TransactionNarrowGroup/TransactionNarrowGroup'
import { formatRelativeDate } from 'helpers/dateTimeHelpers'
import { DATE_FORMAT_READABLE } from 'constants/datetime'
import { RecentTransactionItem } from 'components/features/creditCard/creditCardPage/RecentTransactionItem/RecentTransactionItem'
import Button from 'components/features/pfm/Button/Button'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import Flex from 'components/atoms/Flex/Flex'
import { CardDetailsPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'

const AMOUNT_OF_RECENT_TRANSACTIONS_TO_SHOW = 7

export const RecentTransactions: FC = () => {
  const transactions = useStoreSelector((state) => state.data.creditCard.transactions)

  const transactionsChild = useMemo(() => {
    if (!transactions || transactions.allKeys.length === 0) {
      return null
    }

    const transactionKeys = transactions.allKeys.slice(0, AMOUNT_OF_RECENT_TRANSACTIONS_TO_SHOW)

    return transactionKeys.reduce((acc, uid) => {
      const transaction = transactions.byKey[uid]

      if (acc.prevDate !== transaction.date) {
        acc.children.push(
          <TransactionNarrowGroup key={ `${ transaction.uid }date` } className={ styles.header }>
            { formatRelativeDate(transaction.date, DATE_FORMAT_READABLE) }
          </TransactionNarrowGroup>
        )
        acc.prevDate = transaction.date
      }

      acc.children.push(
        <RecentTransactionItem
          className={ styles.transaction }
          key={ `transaction${ transaction.uid }` }
          transaction={ transaction }
        />
      )

      return acc
    }, {
      prevDate: '',
      children: [] as ReactNode[],
    })
  }, [transactions])?.children ?? null

  return (
    <div className={ styles.recentTransactions }>
      { transactions === null && (
        <LoadingOverlay />
      )}
      { transactionsChild }
      { transactionsChild && (
        <div className={ styles.actions }>
          <div className={ styles.action }>
            <NavigationLink to='transactions'>
              <Button text='See All Transactions' style='hollow' size='small' fullWidth />
            </NavigationLink>
          </div>
          <NavigationLink to='spending'>
            <Button style='textOnlyBig' fullWidth size='tiny'>Learn more about your spending</Button>
          </NavigationLink>
        </div>
      )}
      { !transactionsChild && transactions !== null && (
        <Flex className={ styles.zeroState } verticallyCenter horizontallyCenter>
          <h4>You haven’t made any purchases on your card. </h4>
          <p>
            If you have not received your physical card yet, you can still make purchases online.
          </p>
          <CardDetailsPaneButton text='See your card details' renderAsLink />
        </Flex>
      )}
    </div>
  )
}
